"use client";

import {useCallback} from "react";
import {getWorkflow, saveDraftWorkflow} from "@/actions/workflows/actions";
import type {WorkflowEditorBinding} from "@/contexts/chat/WorkflowEditorBridgeContext";
import {snapshotValuesEqual} from "@/services/wingman/mutation-proposal";
import type {SavedOmegaWorkflow} from "@/services/wingman/omega-results";
import {WorkflowStatus} from "@/enums/workflows";
import {
   publishWorkflowWithSideEffects,
   withStartToolId,
   workflowRegistrationsFromGraph,
   type WorkflowPublication,
} from "@/utils/workflows/workflowPublication";
import type {Workflow} from "@/types/workflows";

/** The saved draft the user reviewed and confirmed in the workflow card. */
export type ConfirmedWorkflowDraft = {
   id: string;
   revision: number;
   name: string;
   description: string;
   graph: SavedOmegaWorkflow["graph"];
};

export type ChatWorkflowPublication =
   | WorkflowPublication
   | {status: "already_published"; workflow: Workflow}
   | {status: "lookup_failed"}
   /**
    * The saved workflow no longer matches the confirmed draft. `current` is the
    * saved draft to review instead, or null when it is gone or not a draft.
    */
   | {status: "stale"; current: ConfirmedWorkflowDraft | null}
   /** The builder beside the chat could not save its pending edits, so nothing was published. */
   | {status: "canvas_not_saved"};

/** A frozen copy of a saved workflow, as the card confirms it and the freshness check compares it. */
export function confirmedDraftOf(workflow: {
   id: string;
   revision?: number | string;
   name: string;
   description?: string | null;
   graph: unknown;
}): ConfirmedWorkflowDraft {
   return {
      id: workflow.id,
      revision: Number(workflow.revision ?? 0),
      name: workflow.name,
      description: workflow.description ?? "",
      graph: structuredClone(workflow.graph) as ConfirmedWorkflowDraft["graph"],
   };
}

/**
 * Publishes a draft confirmed in a chat workflow card through the same path as
 * the builder's Publish button. The card displays the outcome directly.
 * Publication is a user action; Wingman has no tool that publishes.
 *
 * When the builder beside the chat has the workflow open, the canvas is held
 * for the whole publication: its pending edits are saved first (so the
 * freshness check sees them), its autosave and Publish button stay off, and it
 * reloads the result before it is released.
 */
export function useChatWorkflowPublication({
   assistantId,
   editor,
}: {
   /** The conversation's assistant, which receives a newly registered tool. */
   assistantId?: string;
   /** The builder open beside this chat, if any. */
   editor?: WorkflowEditorBinding | null;
}) {
   return useCallback(
      async (confirmed: ConfirmedWorkflowDraft): Promise<ChatWorkflowPublication> => {
         const openEditor = editor?.workflowId === confirmed.id ? editor : null;
         const release = openEditor?.holdCanvas();
         try {
            if (openEditor && !(await openEditor.prepareTurn())) return {status: "canvas_not_saved"};
            const publication = await publishConfirmed(confirmed, assistantId);
            if (publication.status === "published" || publication.status === "already_published") {
               await openEditor?.onDraftSaved();
            }
            return publication;
         } finally {
            release?.();
         }
      },
      [assistantId, editor]
   );
}

async function publishConfirmed(
   confirmed: ConfirmedWorkflowDraft,
   assistantId: string | undefined
): Promise<ChatWorkflowPublication> {
   const current = await getWorkflow(confirmed.id).catch(() => null);
   // A failed read does not prove the draft was removed or published.
   if (!current) return {status: "lookup_failed"};
   if (current.status === WorkflowStatus.Published) return {status: "already_published", workflow: current};
   if (!matchesConfirmedDraft(current, confirmed)) {
      return {status: "stale", current: current?.status === WorkflowStatus.Draft ? confirmedDraftOf(current) : null};
   }

   return publishWorkflowWithSideEffects({
      workflow: current,
      ...workflowRegistrationsFromGraph(current.graph),
      persistToolId: async (toolId, published) => {
         const saved = await saveDraftWorkflow({...published, graph: withStartToolId(published.graph, toolId)});
         if (!saved.data) throw new Error(saved.error ?? "The tool id could not be saved.");
      },
      ...(assistantId ? {attachToAssistantId: assistantId} : {}),
   });
}

export function matchesConfirmedDraft(current: Workflow, confirmed: ConfirmedWorkflowDraft): boolean {
   return (
      current.status === WorkflowStatus.Draft &&
      current.revision === confirmed.revision &&
      current.name === confirmed.name &&
      (current.description ?? "") === confirmed.description &&
      snapshotValuesEqual(current.graph, confirmed.graph)
   );
}
